import { z } from '@hono/zod-openapi';

import { InstantSchema, LocalDateSchema } from './common';
import { PRESET_SCHEMA_VERSION } from './versions';

const SourceReferenceSchema = z
  .object({
    authority: z.string().min(1).max(100),
    url: z.url(),
    checkedAt: LocalDateSchema,
  })
  .strict();

const RegistrationWindowSchema = z
  .object({
    startsAt: InstantSchema,
    endsAt: InstantSchema,
    label: z.string().min(1).max(100),
  })
  .strict()
  .refine((window) => window.startsAt < window.endsAt, {
    message: 'Registration window must end after it starts.',
  });

const ExamScheduleSchema = z
  .object({
    stage: z.string().min(1).max(80),
    examDate: LocalDateSchema,
    registrationWindows: z.array(RegistrationWindowSchema),
  })
  .strict();

const ExamPresetSchema = z
  .object({
    id: z.string().min(1).max(120),
    title: z.string().min(1).max(120),
    category: z.string().min(1).max(80),
    schedules: z.array(ExamScheduleSchema).min(1),
    subjects: z.array(
      z
        .object({
          name: z.string().min(1).max(100),
          defaultUnitLabel: z.enum(['page', 'lecture', 'question', 'custom']),
          totalUnits: z.number().int().positive().optional(),
        })
        .strict(),
    ),
  })
  .strict();

/** Exact payload covered by the detached signature. */
export const ExamPresetPayloadSchema = z
  .object({
    schemaVersion: z.literal(PRESET_SCHEMA_VERSION),
    dataVersion: z.string().min(1).max(100),
    sequence: z.number().int().nonnegative(),
    dataYear: z.number().int().min(2020).max(2200),
    generatedAt: InstantSchema,
    sourceRefs: z.array(SourceReferenceSchema).min(1),
    exams: z.array(ExamPresetSchema).min(1),
  })
  .strict()
  .superRefine((payload, context) => {
    const examIds = new Set<string>();
    for (const [examIndex, exam] of payload.exams.entries()) {
      if (examIds.has(exam.id)) {
        context.addIssue({
          code: 'custom',
          message: `Duplicate exam id: ${exam.id}`,
          path: ['exams', examIndex, 'id'],
        });
      }
      examIds.add(exam.id);

      const schedules = new Set<string>();
      for (const [scheduleIndex, schedule] of exam.schedules.entries()) {
        const key = `${schedule.stage}\u0000${schedule.examDate}`;
        if (schedules.has(key)) {
          context.addIssue({
            code: 'custom',
            message: `Duplicate stage/date pair: ${schedule.stage} ${schedule.examDate}`,
            path: ['exams', examIndex, 'schedules', scheduleIndex],
          });
        }
        schedules.add(key);
      }
    }
  })
  .openapi('ExamPresetPayload');

export const PresetSignatureSchema = z
  .object({
    algorithm: z.literal('ES256'),
    keyId: z.string().min(1).max(100),
    value: z.string().regex(/^[A-Za-z0-9_-]+$/u),
  })
  .strict();

export const ExamPresetBundleSchema = z
  .object({
    payload: ExamPresetPayloadSchema,
    signature: PresetSignatureSchema,
  })
  .strict()
  .openapi('ExamPresetBundle');

export type ExamPresetPayload = z.infer<typeof ExamPresetPayloadSchema>;
export type ExamPresetBundle = z.infer<typeof ExamPresetBundleSchema>;
